import { Box, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import { Notification } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { CollectionFlagBanners } from '@components/CollectionFlag/CollectionFlagBanners';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { useNavigate, useParams } from '@solidjs/router';
import { getCollectionFlagsAction } from '@store/collectionFlags/actions';
import { canFlagCollections } from '@store/collectionFlags/store';
import {
  customerDetailsStore,
  getCustomerData,
  setCustomerDetailsStore,
} from '@store/customers/customerDetails';
import {
  getCreditFlagTypesAction,
  getCustomerCreditFlagsAction,
  getCustomerFlagPermissionsAction,
  getCustomerLTLSettingsLegacy,
} from '@store/customers/customerDetails/LTL';
import { openModal } from '@store/modals';
import { resetCustomerStore } from '@store/orders';
import { ConfigManager } from '@utils/ConfigManager';
import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { reconcile } from 'solid-js/store';

import { CustomerDetailTabPanel, CustomerPageHeader } from './components';
import {
  buildCreatedByLabel,
  getFlagBannerMeta,
} from './components/CustomerPageHeader/components/customerFlagConstants';

const CustomerDetails = () => {
  const [loading, setLoading] = createSignal(true);
  const params = useParams();

  const navigate = useNavigate();

  /**
   * Banners stay visible to anyone who can work either flag type — losing the
   * Financial Exposure flag hides the Add Flag option, not the existing flags.
   */
  const canSeeFlags = () =>
    customerDetailsStore.customerFlagPermissions.canFlagForReview ||
    customerDetailsStore.customerFlagPermissions.canFlagFinancialExposure;

  onMount(async () => {
    resetCustomerStore();
    await Promise.all([
      getCustomerFlagPermissionsAction(),
      getCreditFlagTypesAction(),
    ]);
  });

  // NOTE :- Changed to createEffect from onMount because we are navigating from sidebar
  createEffect(async () => {
    if (params.id) {
      await Promise.all([
        getCustomerLTLSettingsLegacy(params.id),
        getCustomerData(params.id),
        getCustomerCreditFlagsAction(Number(params.id)),
        getCollectionFlagsAction(Number(params.id)),
      ]);
      if (ConfigManager.showLayout && customerDetailsStore.isError) {
        navigate('/customer');
      }
    }
    setLoading(false);
    updateTabPanelHeight();
  });

  const updateTabPanelHeight = () => {
    const header = document.getElementById('customer-details-header');
    const tabPanel = document.getElementById('customer-details-tab-panel');
    if (header && tabPanel) {
      const headerHeight = header.offsetHeight;
      tabPanel.style.height = `calc(100vh - ${headerHeight}px)`;
      tabPanel.style.paddingBottom = '25px';
    }
  };

  onCleanup(() => {
    window.removeEventListener('resize', updateTabPanelHeight);
  });

  return (
    <Show when={!loading()} fallback={<AtgLoader />}>
      <Box
        class="flex justify-between items-center bg-white sticky shadow-lg top-0 z-10"
        id="customer-details-header"
      >
        <CustomerPageHeader />
      </Box>
      <Stack
        class="bg-[#E1ECF2] overflow-y-auto"
        id="customer-details-tab-panel"
      >
        <Show when={canFlagCollections()}>
          <CollectionFlagBanners
            class="m-4 mb-0"
            deleteSource={
              CHANGE_TRACKING_SOURCES.customerDetails.headerCollectionFlagDelete
            }
          />
        </Show>
        <Show when={canSeeFlags()}>
          <For each={customerDetailsStore.customerFlagReviewData}>
            {(flag) => {
              const meta = () => getFlagBannerMeta(flag);
              const createdBy = () => buildCreatedByLabel(flag);
              return (
                <Box class="m-4 mb-0 ">
                  <Notification
                    getCustomStyle={() => meta().containerClass}
                    sxProps="items-start!"
                    onClick={() => {
                      setCustomerDetailsStore(
                        'customerFlagReviewModalModeAndLoading',
                        {
                          mode: 'edit',
                          loading: false,
                        },
                      );
                      setCustomerDetailsStore(
                        'selectedCustomerFlagReviewData',
                        reconcile({ ...flag }),
                      );
                      openModal('flagForReviewModalId');
                    }}
                    type={meta().type}
                    text={
                      <Box display="flex" gap={1} alignItems="center">
                        <Typography
                          variant="body1"
                          component="span"
                          class={`text-sm! font-roboto! font-medium! ${meta().titleClass}`}
                        >
                          {meta().title}
                        </Typography>
                        <Show when={meta().showReviewDate}>
                          <Typography
                            variant="body1"
                            component="span"
                            class=" text-xs! font-roboto! font-medium! text-[#00000099]!"
                          >
                            Review Date{' '}
                            <Typography
                              component="span"
                              variant="body1"
                              class="text-xs! font-roboto! font-normal! text-[#000000DE]!"
                            >
                              {flag.reviewDate}
                            </Typography>
                          </Typography>
                        </Show>
                      </Box>
                    }
                    subText={
                      <Box class="flex flex-col">
                        <Typography
                          component="span"
                          variant="body1"
                          class="text-sm! font-roboto! font-normal! text-[#000000DE]! break-all!"
                        >
                          {flag.description}
                        </Typography>
                        <Show when={createdBy()}>
                          <Typography
                            component="span"
                            variant="body1"
                            class="text-xs! font-roboto! font-normal! text-[#00000099]!"
                          >
                            {createdBy()}
                          </Typography>
                        </Show>
                      </Box>
                    }
                  />
                </Box>
              );
            }}
          </For>
        </Show>
        <Box class="m-2 p-2 bg-white mb-[80px] h-full overflow-y-auto!">
          <CustomerDetailTabPanel />
        </Box>
      </Stack>
    </Show>
  );
};

export default CustomerDetails;
